/**
 * This displays a three-vertical-dot icon which when clicked, shows a dropdown menu offering
 * various actions and options to the user with optional keyboard shortcuts.
 */
import * as preact from "preact";

import * as browserTTSVoices from "browserTTSVoices";
import UserWordModel from "models/UserWordModel";
import Check from "views/Check";

type MenuItem =
  | {
      type: "action";
      label: string;
      onClick: () => void;
      disabled?: boolean;
    }
  | {
      type: "toggle";
      label: string;
      onToggle: (value: boolean) => boolean | void; // Return false to prevent the toggle
      initialValue: boolean;
    }
  | { type: "separator" };

interface Props {
  items: MenuItem[];
}

interface State {
  open: boolean;
  toggleStates: boolean[];
}

class DropdownMenu extends preact.Component<Props, State> {
  userWords: Backbone.Collection<UserWordModel>;

  constructor(props: Props) {
    super(props);

    this.state = {
      open: false,
      toggleStates: props.items.map((item) =>
        item.type === "toggle" ? item.initialValue : false
      ),
    };

    window.document.addEventListener("click", this.documentClickHandler);
  }

  dropdownMenuClassName = "dropdownMenu";

  documentClickHandler = (event: MouseEvent) => {
    // If the user clicks outside the dropdown menu, close it
    const target = event.target as Element;
    if (this.state.open && !target.closest(`.${this.dropdownMenuClassName}`)) {
      this.setState({
        open: false,
      });
    }
  };

  render() {
    return (
      <div class={`dropdownMenuContainer${this.state.open ? " open" : ""}`}>
        <i
          class="icon icon-ellipsis-vertical menuButton"
          onClick={(event) => {
            event.stopPropagation();
            this.setState({
              open: !this.state.open,
            });
          }}
        ></i>
        {this.state.open ? (
          <ul class={`${this.dropdownMenuClassName}`}>
            {this.props.items.map((item, index) => {
              switch (item.type) {
                case "action":
                  return (
                    <li
                      class={`actionOrToggle${item.disabled ? " disabled" : ""}`}
                      onClick={
                        item.disabled
                          ? undefined
                          : () => {
                              item.onClick();
                              this.setState({ open: false });
                            }
                      }
                    >
                      <span class="iconContainer"></span>
                      <span>{item.label}</span>
                    </li>
                  );

                case "toggle":
                  return (
                    <li
                      class="actionOrToggle"
                      onClick={() => {
                        if (
                          item.onToggle(!this.state.toggleStates[index]) ===
                          false
                        ) {
                          return;
                        }

                        this.setState({
                          toggleStates: this.state.toggleStates.map(
                            (value, i) => (i === index ? !value : value)
                          ),
                        });
                      }}
                    >
                      <span class="iconContainer">
                        {this.state.toggleStates[index] ? <Check /> : null}
                      </span>
                      <span>{item.label}</span>
                    </li>
                  );

                case "separator":
                  return (
                    <li class="separator">
                      <span class="iconContainer"></span>
                      <span class="dividerLine"></span>
                    </li>
                  );
              }
            })}
          </ul>
        ) : null}
      </div>
    );
  }

  componentWillUnmount() {
    window.document.removeEventListener("click", this.documentClickHandler);
  }
}

export default DropdownMenu;
